import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Kicker } from "@/components/primitives";
import { apiClient, formatApiError } from "@/lib/auth";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("This reset link is missing its token."); return; }
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setErr("Passwords don&apos;t match — try again."); return; }
    setBusy(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/auth", { replace: true }), 2400);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, e2.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-8" data-testid="reset-page">
      <Link to="/auth" className="self-start chip mb-6 max-w-3xl w-full sm:w-auto" data-testid="reset-back">
        <ArrowLeft size={14} /> Back to sign in
      </Link>

      <div className="h-card w-full max-w-md px-6 sm:px-10 py-10 shadow-seal animate-bloom-in" data-testid="reset-card">
        <div className="flex justify-center mb-5"><BrandLogo size={80} /></div>
        <Kicker>Choose a new password</Kicker>
        <h1 className="font-display text-4xl sm:text-5xl text-sage-dark text-center mt-3 leading-tight">
          Back into the patch
        </h1>
        <p className="text-center font-display italic text-ink-soft mt-2">
          Pick something you&apos;ll remember next time.
        </p>

        {done ? (
          <div className="mt-8 text-center space-y-3" data-testid="reset-success">
            <CheckCircle2 size={36} className="text-sage-dark mx-auto" />
            <p className="text-ink leading-relaxed">Your password has been reset.</p>
            <p className="text-sm text-ink-soft">Taking you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">New password</span>
              <input
                type="password"
                className="field"
                placeholder="At least 6 characters"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="reset-password"
                autoComplete="new-password"
                required
              />
            </label>
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Confirm password</span>
              <input
                type="password"
                className="field"
                placeholder="Type it again"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                autoComplete="new-password"
                required
              />
            </label>
            {err && (
              <div className="bg-cream-deep border border-heritage-border rounded-xl px-4 py-3 text-sm text-terracotta-dark" data-testid="reset-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              className="btn-heritage w-full mt-2"
              disabled={busy || !token}
              data-testid="reset-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              {busy ? "Setting password…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
